import React, { useState, useRef, useEffect } from "react";
import ModifyStyle from "../../components/plan/PlanStyle";
import PlanService from "../../services/PlanService";
import MessageContext from "../../providers/GlobalTip";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import {
  Button,
  Box,
  ButtonGroup,
  IconButton,
} from "@mui/material";
import _ from "underscore";
import __ from "lodash";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlanCopyFromModal from "./PlanCopyFromModal";
import {
  HotKeysContext,
} from "../../providers/HotKeysProvider";
import PlanChart from "./PlanChart";
import { trackEvent } from "../../services/AmplitudeService";
import PlanEditTable from "./PlanEditTable";
import TrendTargetBox from "./TrendTargetBox";

function LivePlanEdit(props) {
  const {
    visualVariant,
    epochMonth,
    client,
    onPlanDataLoad,
    clientList,
    setShowHeaderContainer,
    isFuturePlan,
    planId,
    locationClassId,
    addPlanVersionToClassDropdown,
    nextClass,
    nextLocation,
    forceReload,
    setForceReload,
    onFuturePlanDetected,
  } = props;
  const isV3 = visualVariant === 'v3';
  const [displayMonths, setDisplayMonths] = useState([]);
  const messageContext = MessageContext.useMessage();
  const [displayCopyFromModal, setDisplayCopyFromModal] = useState(false);
  const [displayCopyToModal, setDisplayCopyToModal] = useState(false);
  const intervalRef = useRef();
  const storedLeftPanelVisibility = JSON.parse(localStorage.getItem('showPlanningLeftPanel'));
  const isCopy = JSON.parse(localStorage.getItem('showLoadCopyContainer'))
  const isTarget = JSON.parse(localStorage.getItem('showTargetContainer'))
  const isGraph = JSON.parse(localStorage.getItem('showGraphContainer'))
  const [showLeftPanel, setShowLeftPanel] = useState(storedLeftPanelVisibility === null ? true : storedLeftPanelVisibility);
  const [showLoadCopyContainer, setShowLoadCopyContainer] = useState(isCopy === null ? true : isCopy);
  const [showTargetContainer, setShowTargetContainer] = useState(isTarget === null ? true : isTarget);
  const [showGraphContainer, setShowGraphContainer] = useState(isGraph === null ? true : isGraph);
  const [futurePlanModalOpen, setFuturePlanModalOpen] = useState(false);

  const [chartData, setChartData] = useState(new Map());
  const [overridePlanData, setOverridePlanData] = useState(null); // used during copy from to override the plan data shown in the table without updating the plan id state

  const [tabs, setTabs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    localStorage.setItem('showPlanningLeftPanel', JSON.stringify(showLeftPanel));
    localStorage.setItem('showLoadCopyContainer', JSON.stringify(showLoadCopyContainer));
    localStorage.setItem('showTargetContainer', JSON.stringify(showTargetContainer));
    localStorage.setItem('showGraphContainer', JSON.stringify(showGraphContainer));
  }, [showLeftPanel, showLoadCopyContainer, showTargetContainer, showGraphContainer]);

  useEffect(() => {
    return () => {
      // componentwillunmount in functional component.
      clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!displayMonths.length && epochMonth) {
      const startMonth = PlanService.convertEpochMonthToDate(epochMonth);

      // get this and next 11 months, format as MMM
      let months = [];
      for (let i = 0; i < 12; i++) {
        months.push(moment(startMonth).add(i, 'months').format('MMM'));
      }
      setDisplayMonths(months);
    }
  }, [epochMonth]);

  const toggleCopyPanel = () => {
    setShowLoadCopyContainer(showLoadCopyContainer => !showLoadCopyContainer);
  }
  const toggleLeftPanel = () => {
    setShowLeftPanel(showLeftPanel => !showLeftPanel);
  }
  const toggleTargetPanel = () => {
    setShowTargetContainer(showTargetContainer => !showTargetContainer);
  }
  const toggleGraphPanel = () => {
    setShowGraphContainer(showGraphContainer => !showGraphContainer);
  }
  const toggleHeaderPanel = () => {
    setShowHeaderContainer(showHeaderContainer => !showHeaderContainer);
  }

  const [savingPlan, setSavingPlan] = useState(false);

  const updatePlanV2 = async (data, annuals, asFuture = false) => {
    // check if any inflow are negative
    let hasMinusInflow = false;

    if (!data["InflowRetailPlan"] || !data["InflowCostPlan"]) {
      messageContext.show("Inflow data is missing");
      return false;
    }

    data["InflowRetailPlan"].forEach((item) => {
      if (item < 0) {
        hasMinusInflow = true;
      }
    });

    data["InflowCostPlan"].forEach((item) => {
      if (item < 0) {
        hasMinusInflow = true;
      }
    });

    if (hasMinusInflow) {
      messageContext.show("Negative inflow error!");
      return false;
    }

    if (asFuture) {
      data["isFuturePlan"] = true;
      data["planVersion"] = 1000; // set to 1000 so back end logic can deal with plan version iteration
    }

    setSavingPlan(true);

    try {
      // append annuals to data
      data.annuals = annuals;

      // Prefer UI-provided model selection (from AnnualEditChip) if present.
      // Fall back to legacy value-based mapping for backward compatibility.
      const hasUiModelTypes = !!(
        data.annuals && (
          data.annuals.sales_model_type != null ||
          data.annuals.markdowns_model_type != null ||
          data.annuals.turn_model_type != null ||
          data.annuals.imu_model_type != null
        )
      );

      let models = {
        sales_model_type: 0,
        markdowns_model_type: 0,
        turn_model_type: 0,
        imu_model_type: 0,
      };

      if (hasUiModelTypes) {
        models = {
          sales_model_type: Number(data.annuals?.sales_model_type ?? 0),
          markdowns_model_type: Number(data.annuals?.markdowns_model_type ?? 0),
          turn_model_type: Number(data.annuals?.turn_model_type ?? 0),
          imu_model_type: Number(data.annuals?.imu_model_type ?? 0),
        };
      } else {
        // check which models are being used (legacy: infer from matching values)
        switch (data.annuals?.annualSalesPlan) {
          case data.annuals?.annualSalesL12:
            models.sales_model_type = 1;
            break;
          case data.annuals?.linearSalesModel:
            models.sales_model_type = 2;
            break;
          case data.annuals?.calc1SalesModel:
            models.sales_model_type = 3;
            break;
          case data.annuals?.calc3SalesModel:
            models.sales_model_type = 4;
            break;
          default:
            models.sales_model_type = 0;
        }

        switch (data.annuals?.annualMarkdownsPlan) {
          case data.annuals?.annualMarkdownsL12:
            models.markdowns_model_type = 2;
            break;
          default:
            models.markdowns_model_type = 0;
        }

        switch (data.annuals?.annualTurnPlan) {
          case data.annuals?.annualTurnActual:
            models.turn_model_type = 1;
            break;
          case data.annuals?.turnLm:
            models.turn_model_type = 2;
            break;
          default:
            models.turn_model_type = 0;
        }

        switch (data.annuals?.annualImuPlan) {
          case data.annuals?.imuActual:
            models.imu_model_type = 2;
            break;
          case data.annuals?.imuLm:
            models.imu_model_type = 1;
            break;
          default:
            models.imu_model_type = 0;
        }
      }

      data.modelTypes = models;

      let response = await PlanService.updatePlanV2(client.Id, planId, data);

      messageContext.showSuccess("Updated successfully. Attempting to load plan...");
      addPlanVersionToClassDropdown(response.result.plan_id, response.result.plan_version, data.clientLocationId, data.locationClassId, data.serviceTypeId)

      trackEvent('Planning Pages Event', {
        action: 'Update Plan',
        clientCode: client?.ClientCode,
        newPlanVersion: response.result.plan_version,
      })
    } catch (e) {
      if (e?.response?.data?.message) {
        messageContext.show(e.response.data.message);
      } else {
        messageContext.show("Unknown Error");
      }
      console.log(e)
    }

    setSavingPlan(false);
  };

  const showCopyToModal = () => {
    setDisplayCopyToModal(true);
  };

  const hotKeysContext = {
    updatePlanV2,
    nextLocation,
    nextClass,
    setDisplayCopyFromModal,
    showCopyToModal,
    toggleCopyPanel,
    toggleTargetPanel,
    toggleGraphPanel,
    toggleHeaderPanel,
  };

  return (
    <>
      <Box
        sx={{
          ...ModifyStyle.leftSection,
          width: showLeftPanel ? ModifyStyle.leftSection.width : '48px',
          minWidth: showLeftPanel ? ModifyStyle.leftSection.width : '48px',
          transition: 'width 0.2s ease, min-width 0.2s ease',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            ...ModifyStyle.leftContent,
            display: 'flex',
            flexDirection: 'column',
            alignItems: showLeftPanel ? 'stretch' : 'center',
          }}
          className={isV3 ? 'v3-plan-card' : undefined}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: showLeftPanel ? 'flex-end' : 'center',
              pt: 1,
              px: 1,
            }}
          >
            <IconButton
              aria-label={showLeftPanel ? 'Hide planning left panel' : 'Show planning left panel'}
              data-testid="planning-left-panel-toggle"
              onClick={toggleLeftPanel}
              size="small"
            >
              {showLeftPanel ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
          {showLeftPanel && (
            <>
              <Box sx={ModifyStyle.loadPlan} className={isV3 ? 'v3-plan-page__callout' : undefined}>
                <ButtonGroup id={'left-button-group'}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2, ...(isV3 ? { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '14px', p: 0.5 } : {}) }}
                  aria-label="outlined button group"
                >
                  <Button variant={showLoadCopyContainer ? "contained" : "outlined"} onClick={() => toggleCopyPanel()}><ContentCopyIcon sx={{ fontSize: '12px' }} />  Copy</Button>
                  <Button variant={showTargetContainer ? "contained" : "outlined"} onClick={() => toggleTargetPanel()}><TrackChangesIcon sx={{ fontSize: '12px' }} />  Target</Button>
                  <Button variant={showGraphContainer ? "contained" : "outlined"} onClick={() => toggleGraphPanel()}><InsertChartIcon sx={{ fontSize: 'small' }} />  Chart</Button>
                </ButtonGroup>
                {showLoadCopyContainer && <Box id={'load-copy-container'}>
                  <Button
                    onClick={() => setDisplayCopyFromModal(true)}
                    fullWidth
                    variant="outlined"
                  >
                    Load Plan
                  </Button>
                  <PlanCopyFromModal
                    open={displayCopyFromModal}
                    onClose={() => setDisplayCopyFromModal(false)}
                    clientList={clientList}
                    setOverridePlanData={setOverridePlanData}
                  />
                </Box>}
              </Box>
              <TrendTargetBox data={tabs} />
              {showGraphContainer && chartData && chartData.size > 0 && <Box id={"graph-container"} sx={ModifyStyle.graphContainer} className={isV3 ? 'v3-plan-card' : undefined}>
                <PlanChart sx={{ mt: 2 }} displayMonths={displayMonths} chartData={chartData} />
              </Box>}
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          ...ModifyStyle.rightSection,
          width: showLeftPanel ? ModifyStyle.rightSection.width : 'calc(100% - 48px)',
          transition: 'width 0.2s ease',
        }}
        className={isV3 ? 'v3-plan-card' : undefined}
      >
        <Box sx={{ pb: '20px' }}>
          <HotKeysContext.Provider value={hotKeysContext}>
            <PlanEditTable
              visualVariant={visualVariant}
              nextClass={nextClass}
              nextLocation={nextLocation}
              savingPlan={savingPlan}
              onUpdate={updatePlanV2}
              client={client}
              planId={planId}
              locationClassId={locationClassId}
              epochMonth={epochMonth}
              setChartData={setChartData}
              displayMonths={displayMonths}
              messageContext={messageContext}
              tabs={tabs}
              setTabs={setTabs}
              overridePlanData={overridePlanData}
              forceReload={forceReload}
              setForceReload={setForceReload}
              onFuturePlanDetected={onFuturePlanDetected}
              showLeftPanel={showLeftPanel}
            />
          </HotKeysContext.Provider>
        </Box>
      </Box>
    </>
  );
}

export default LivePlanEdit;
